/**
 * The always-on inner-power aura (ไอกำลังภายใน): what an entity's aura looks like, from the neigong it trains and how
 * far (data below + read-only World state), and its frames, painted once per (look, level band, figure height).
 *
 * Pixel rules (render/fx/pixelRules.ts): drawn on the scene grid (one canvas pixel = one art pixel), every tone from the
 * master-palette FX ramps, hard 1-px outlines in the ramp's darkest tone, no see-through glow (dither instead), held
 * frames. Three bands:
 *   - low  (to 40 % of the neigong's levels): a few faint wisps rising slowly beside the body;
 *   - mid  (to 70 %): a clear dashed rim round the body rising, and motes circling the waist;
 *   - high (above): a thick flame-shaped aura with rising licks, and a ripple on the ground under the feet.
 * A mastered neigong (max level) carries its own mastery aura (render/auras.ts) instead.
 */
import { ARENA_ROSTER, type Entity, NEIGONG, type World } from '@nine/shared';
import { canvas, ctx2d } from './pixel';

// ── data: the neigong's colour ──

/** Ramps, dark → light; index 0 is the outline. Every tone is on the FX master palette (pixelRules FX_RAMPS). */
export const AURA_RAMPS = {
  mist: ['#121f3c', '#4f9cc8', '#8ccbe6', '#cdeaf4', '#e2f2f0', '#fdf6ec'],
  ember: ['#2e0e0c', '#a8401e', '#d8682a', '#f09a48', '#f8c878', '#fdecc0'],
  gold: ['#2e1e0e', '#9a6732', '#c08c48', '#e4b575', '#f6dca0', '#fff1cc'],
  ink: ['#0e0a10', '#1e1430', '#34234e', '#503a70', '#725894', '#9a80b4'],
  moon: ['#1e1430', '#725894', '#9a80b4', '#c8b0d6', '#efe2f4', '#fdf6ec'],
  tide: ['#121f3c', '#1d3f6e', '#2f6aa0', '#4f9cc8', '#8ccbe6', '#cdeaf4'],
  frost: ['#1e2430', '#708094', '#9aa8b8', '#c8d2dc', '#e2e8ea', '#fdf6ec'],
  jade: ['#0f2a2a', '#2a6660', '#42897c', '#6cae98', '#a4d4bc', '#dcf2e2'],
  blood: ['#2a0a14', '#671f2c', '#87232a', '#c4272d', '#e55546', '#f48a6c'],
  sword: ['#1e2430', '#4e5c70', '#708094', '#9aa8b8', '#c8d2dc', '#e2e8ea'],
} as const satisfies Record<string, readonly string[]>;
export type AuraTint = keyof typeof AURA_RAMPS;

/** Each neigong's aura colour; one not listed takes its element's. */
export const NEIGONG_TINT: Readonly<Record<string, AuraTint>> = {
  'blood-oath': 'blood',
  'crimson-sky': 'blood',
  'cloud-heart': 'mist',
  'cloud-void': 'mist',
  'mist-breath': 'mist',
  'mist-wheel': 'mist',
  'mist-crown': 'mist',
  'bell-body': 'gold',
  'bell-layers': 'gold',
  'bell-silent': 'gold',
  'road-iron': 'gold',
  'lamp-spark': 'gold',
  'ember-heart': 'ember',
  'ember-sea': 'ember',
  'lantern-night': 'ember',
  'ink-breath': 'ink',
  'ink-stillness': 'ink',
  'ink-boundless': 'ink',
  'mourning-heart': 'moon',
  'vigil-heart': 'moon',
  'moon-veil': 'moon',
  'tide-rhythm': 'tide',
  'tide-swell': 'tide',
  'tide-root': 'tide',
  'snow-hush': 'frost',
  'pine-stand': 'frost',
  'frost-root': 'frost',
  'nine-heart': 'sword',
};
const ELEMENT_TINT: Readonly<Record<string, AuraTint>> = {
  yang: 'ember',
  yin: 'moon',
  soft: 'tide',
  hard: 'gold',
  taiji: 'jade',
};

export const tintOf = (neigong: string): AuraTint =>
  NEIGONG_TINT[neigong] ?? ELEMENT_TINT[NEIGONG[neigong]?.element ?? ''] ?? 'mist';

/** 0 low, 1 mid, 2 high. */
export type AuraBand = 0 | 1 | 2;
export const bandOf = (level: number, max: number): AuraBand => {
  const k = level / Math.max(1, max);
  return k < 0.4 ? 0 : k < 0.7 ? 1 : 2;
};

// ── who trains what (read-only) ──

export interface InnerPower {
  neigong: string;
  level: number;
  max: number;
}

let arenaByName: Map<string, InnerPower> | null = null;

/**
 * The neigong an entity trains and its level, or null. The player's active neigong from their progress; an arena
 * contender's (also on the duel ground) from its build, matched by name. Other entities carry no neigong level in
 * the World's state.
 */
export function innerPowerOf(w: World, e: Entity): InnerPower | null {
  if (e === w.player) {
    const pr = w.progress;
    const id = pr.activeNeigong;
    const n = NEIGONG[id];
    const level = pr.neigong[id] ?? 0;
    return n && level > 0 ? { neigong: id, level, max: n.maxLevel } : null;
  }
  if (e.kind !== 'rival' && e.kind !== 'npc') return null;
  if (!arenaByName) {
    arenaByName = new Map();
    for (const b of ARENA_ROSTER) {
      const n = NEIGONG[b.neigong];
      if (n) arenaByName.set(b.name, { neigong: b.neigong, level: b.neigongLevel, max: n.maxLevel });
    }
  }
  return arenaByName.get(e.name) ?? null;
}

// ── frames ──

/** Frames in a loop. */
export const NF = 16;
/** Frames per second, by band: the low wisps drift slowly, the high flame licks quickly. */
export const BAND_FPS: readonly [number, number, number] = [7, 9, 12];

export interface AuraFrames {
  /** NF frames side by side, each `w` × `h`; the feet at (`ax`, `ay`) in a frame. */
  body: HTMLCanvasElement;
  /** The ripple on the ground (high band), its centre at (`gx`, `gy`) in a frame. */
  ground: HTMLCanvasElement | null;
  w: number;
  h: number;
  ax: number;
  ay: number;
  gw: number;
  gh: number;
  gx: number;
  gy: number;
}

const CACHE = new Map<string, AuraFrames>();
/** Bytes of frames painted so far (for the budget table, docs/research/TECHART_BUDGETS.md). */
export const auraFrameBytes = (): number => {
  let n = 0;
  for (const f of CACHE.values()) n += (f.body.width * f.body.height + (f.ground ? f.ground.width * f.ground.height : 0)) * 4;
  return n;
};

/** The frames for a look, band and figure height (art px, whole even number), painted on first use. */
export function auraFrames(tint: AuraTint, band: AuraBand, figH: number): AuraFrames {
  const key = `${tint}|${band}|${figH}`;
  let f = CACHE.get(key);
  if (!f) CACHE.set(key, (f = paint(AURA_RAMPS[tint], band, figH)));
  return f;
}

const TAU = Math.PI * 2;
function hash(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A frame being painted: tone indices (0 empty, 1 + ramp index) and which pixels want an outline round them. */
class Buf {
  readonly tone: Uint8Array;
  readonly ol: Uint8Array;
  /** Pixels kept empty (a dithered core's holes): never outlined. */
  readonly hole: Uint8Array;
  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.tone = new Uint8Array(w * h);
    this.ol = new Uint8Array(w * h);
    this.hole = new Uint8Array(w * h);
  }
  set(x: number, y: number, t: number, outline = false): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = y * this.w + x;
    this.tone[i] = t + 1;
    this.ol[i] = outline ? 1 : 0;
  }
  /** Sets only where nothing is yet. */
  under(x: number, y: number, t: number): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h || this.tone[y * this.w + x]) return;
    this.tone[y * this.w + x] = t + 1;
  }
  /** Rings outlined pixels with the ramp's outline tone (index 0). */
  outline(): void {
    const { w, h, tone, ol, hole } = this;
    const add: number[] = [];
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (tone[i] || hole[i]) continue;
        if (
          (x > 0 && ol[i - 1]) ||
          (x < w - 1 && ol[i + 1]) ||
          (y > 0 && ol[i - w]) ||
          (y < h - 1 && ol[i + w])
        )
          add.push(i);
      }
    for (const i of add) tone[i] = 1;
  }
  into(img: ImageData, ox: number, ramp: readonly (readonly [number, number, number])[]): void {
    const { w, h, tone } = this;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const t = tone[y * w + x]!;
        if (!t) continue;
        const c = ramp[t - 1]!;
        const o = (y * img.width + ox + x) * 4;
        img.data[o] = c[0];
        img.data[o + 1] = c[1];
        img.data[o + 2] = c[2];
        img.data[o + 3] = 255;
      }
  }
}

/** The body's half width at a height `t` (0 feet, 1 top of the head), as a fraction of the shoulders'. */
function bodyShape(t: number): number {
  if (t < 0.12) return 0.72 + t * 1.5;
  if (t < 0.74) return 1;
  if (t < 0.8) return 1 - (t - 0.74) * 5;
  return 0.7;
}

function paint(css: readonly string[], band: AuraBand, figH: number): AuraFrames {
  const ramp = css.map((c) => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16)) as unknown as [number, number, number]);
  const hw = Math.max(6, Math.round(figH * 0.19));
  const pad = band === 2 ? 5 : 3;
  const top = band === 2 ? 14 : band === 1 ? 9 : 7;
  const w = 2 * (hw + pad + 5) + 1;
  const h = figH + top + 3;
  const ax = (w - 1) / 2;
  const ay = figH + top;
  const body = canvas(w * NF, h);
  const bimg = new ImageData(w * NF, h);
  // The ground ripple: an iso ring (2:1) widening from the feet.
  const rMax = hw + pad + 4;
  const gw = 2 * rMax + 3;
  const gh = rMax + 3;
  const gx = (gw - 1) / 2;
  const gy = (gh - 1) / 2;
  const ground = band === 2 ? canvas(gw * NF, gh) : null;
  const gimg = ground ? new ImageData(gw * NF, gh) : null;

  for (let f = 0; f < NF; f++) {
    const p = f / NF;
    const b = new Buf(w, h);
    if (band === 2) paintFlame(b, p, hw, pad, figH, ax, ay);
    if (band === 1) paintRim(b, p, hw, pad, figH, ax, ay);
    if (band >= 1) paintMotes(b, p, hw, pad, figH, ax, ay, band === 2 ? 5 : 4);
    paintWisps(b, p, hw, figH, ax, ay, band === 0 ? 4 : 2, band === 0 ? 0 : 2);
    b.outline();
    b.into(bimg, f * w, ramp);
    if (gimg) {
      const gb = new Buf(gw, gh);
      paintRipple(gb, p, rMax, gx, gy);
      gb.into(gimg, f * gw, ramp);
    }
  }
  ctx2d(body).putImageData(bimg, 0, 0);
  if (ground && gimg) ctx2d(ground).putImageData(gimg, 0, 0);
  return { body, ground, w, h, ax, ay, gw, gh, gx, gy };
}

/** Wisps: short streaks rising beside the body, brightest at the head, gone near the top. */
function paintWisps(b: Buf, p: number, hw: number, figH: number, ax: number, ay: number, n: number, salt: number): void {
  for (let i = 0; i < n; i++) {
    const side = i % 2 ? 1 : -1;
    const q = (p + i / n + hash(i, salt) * 0.2) % 1;
    const x0 = ax + side * (hw + 1 + Math.round(hash(i, 7 + salt) * 3));
    const y = ay - figH * (0.12 + 0.95 * q);
    const x = x0 + Math.round(Math.sin(TAU * (q * 1.5 + i * 0.3)) * 1.5);
    const len = q > 0.8 ? 1 : q > 0.6 ? 2 : 3;
    b.set(x, y, 4);
    if (len > 1) b.set(x, y + 1, 3);
    if (len > 2) b.set(x - side * (q > 0.4 ? 1 : 0), y + 2, 2);
  }
}

/** A row's envelope half width round the body (art px), `t` its height as a fraction of the figure's. */
function envelope(t: number, hw: number, pad: number, figH: number, over: number): number {
  if (t <= 1) return hw * bodyShape(t) + pad;
  // Over the head: a dome closing `over` px above it.
  const k = ((t - 1) * figH) / over;
  return k >= 1 ? -1 : (hw * 0.7 + pad) * Math.sqrt(1 - k * k);
}

/** Mid: a 1-px rim round the body in rising dashes, a deep tone outside it. */
function paintRim(b: Buf, p: number, hw: number, pad: number, figH: number, ax: number, ay: number): void {
  const over = pad + 3;
  for (let y = ay; y >= ay - figH - over; y--) {
    const t = (ay - y) / figH;
    const e = envelope(t, hw, pad, figH, over);
    if (e < 0) continue;
    // Dashes 4 on, 2 off, scrolling up 12 px a loop.
    const on = (((y + Math.round(p * 12)) % 6) + 6) % 6 < 4;
    if (!on) continue;
    const fade = t < 0.08; // the rim thins out at the feet
    for (const side of [-1, 1]) {
      const x = Math.round(ax + side * e);
      if (fade && (y & 1)) continue;
      b.set(x, y, side > 0 ? 4 : 3);
      b.under(x + side, y, 1);
    }
  }
  // The dome's top as a row, so the rim closes over the head.
  const yTop = ay - figH - over + 1;
  for (let x = -2; x <= 2; x++) if ((((yTop + Math.round(p * 12)) % 6) + 6) % 6 < 4) b.set(ax + x, yTop, 3);
}

/** High: a thick flame — outlined, mid edge, light ring, dithered core, licks rising inside, tongues over the head. */
function paintFlame(b: Buf, p: number, hw: number, pad: number, figH: number, ax: number, ay: number): void {
  const over = pad + 4;
  const { w, h } = b;
  const inside = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) {
    const t = (ay - y) / figH;
    if (t < 0) continue;
    for (const side of [-1, 1]) {
      // Licks along the sides, rising 18 px a loop.
      const lick = Math.max(0, Math.sin(TAU * ((y + p * 18) / 9) + (side > 0 ? 1.9 : 0)));
      const e = envelope(t, hw, pad, figH, over) + (t > 0.1 && t <= 1 ? Math.round(lick * 2) : 0);
      if (e < 0) continue;
      for (let x = 0; x <= e; x++) {
        const px = Math.round(ax + side * x);
        if (px >= 0 && px < w) inside[y * w + px] = 1;
      }
    }
  }
  // Tongues over the head: three columns of flame, their tips flickering.
  for (let k = -1; k <= 1; k++) {
    const cx = Math.round(ax + k * (hw * 0.45));
    const tip = 4 + Math.round(3 * (0.5 + 0.5 * Math.sin(TAU * (p * 2 + k * 0.33)))) - Math.abs(k);
    const base = Math.round(ay - figH - over * Math.sqrt(1 - (k * 0.45 * hw) ** 2 / (hw * 0.7 + pad) ** 2));
    for (let d = 0; d < tip + 2; d++) {
      const half = d < tip - 2 ? 1 : 0;
      for (let x = -half; x <= half; x++) {
        const px = cx + x + (d > tip - 1 ? k : 0),
          py = base - d + 1;
        if (px >= 0 && px < w && py >= 0 && py < h) inside[py * w + px] = 1;
      }
    }
  }
  // Depth from the edge (4-neighbour erosion, up to 3).
  const depth = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) depth[i] = inside[i] ? 9 : 0;
  for (let pass = 1; pass <= 3; pass++)
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (depth[i] !== 9) continue;
        const out = (j: number, ok: boolean) => !ok || (depth[j]! > 0 && depth[j]! < pass) || depth[j] === 0;
        if (out(i - 1, x > 0) || out(i + 1, x < w - 1) || out(i - w, y > 0) || out(i + w, y < h - 1)) depth[i] = pass;
      }
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const d = depth[i]!;
      if (!d) continue;
      // Lit from the upper right: that edge a step lighter.
      const lit = x > ax ? 1 : 0;
      if (d === 1) b.set(x, y, 2 + lit, true);
      else if (d === 2) b.set(x, y, 3 + lit, true);
      else if ((x + y) & 1) b.set(x, y, 3, true);
      else b.hole[i] = 1;
    }
  // Licks: short bright streaks rising up the flame.
  for (let i = 0; i < 6; i++) {
    const q = (p * 2 + i / 6) % 1;
    const side = i % 2 ? 1 : -1;
    const x = Math.round(ax + side * (hw + 1 + (i % 3)));
    const y = Math.round(ay - figH * (0.1 + 0.95 * q));
    for (let d = 0; d < 3; d++) if (inside[(y + d) * w + x]) b.set(x, y + d, d ? 4 : 5, true);
  }
}

/** Motes circling the waist on a tilted ring, bobbing; 2x2, outlined. */
function paintMotes(b: Buf, p: number, hw: number, pad: number, figH: number, ax: number, ay: number, n: number): void {
  const rx = hw + pad + 3;
  for (let i = 0; i < n; i++) {
    const a = TAU * (p + i / n);
    const x = Math.round(ax + Math.cos(a) * rx);
    const y = Math.round(ay - figH * 0.42 + Math.sin(a) * 3 - Math.sin(TAU * (p * 2 + i * 0.37)) * 2);
    b.set(x, y, 5, true);
    b.set(x + 1, y, 4, true);
    b.set(x, y + 1, 4, true);
    b.set(x + 1, y + 1, 3, true);
  }
}

/** The ground ripple: two iso rings widening from the feet, bright young, dithered out old. */
function paintRipple(b: Buf, p: number, rMax: number, gx: number, gy: number): void {
  for (let k = 0; k < 2; k++) {
    const q = (p + k / 2) % 1;
    const r = 3 + q * (rMax - 3);
    const tone = q < 0.3 ? 4 : q < 0.65 ? 3 : 2;
    const steps = Math.ceil(r * 8);
    let last = -1;
    for (let s = 0; s < steps; s++) {
      const a = (s / steps) * TAU;
      const x = Math.round(gx + Math.cos(a) * r);
      const y = Math.round(gy + Math.sin(a) * r * 0.5);
      const id = y * b.w + x;
      if (id === last) continue;
      last = id;
      if (q > 0.65 && (x + y) & 1) continue;
      b.set(x, y, tone);
    }
  }
}
